import { type fabric } from "fabric"
import type Sign from "../sign"
import { isSpotColor, spotColorName } from "../spot_colors"
import { type DrawableMixin, type DrawableClasses } from "./drawable_mixin"
import CircleObject from "@/signomatic/app/objects/circle"
import PathObject from "@/signomatic/app/objects/path"
import PolygonObject from "@/signomatic/app/objects/polygon"
import { arrayUniq } from "@/signomatic/util"
import { timingMarkStart, timingMarkEnd } from "@/signomatic/util/logger"
import pathRounding from "@/signomatic/util/path_rounding"

type ObjectClasses = "object" | "image" | "text" | "slow-printer-font"

export interface ObjectMixin<ST> extends fabric.Object {
  attachedSign: Sign
  state: ST
  hasObjectBorder: boolean
  classes: ObjectClasses[]

  /**
   * Adds css class to be used in SVG
   */
  addClass(...classNames: ObjectClasses[]): this

  /**
   * Attach Sign instance to this drawable to use for clipping in SVG
   */
  attachSign(sign: Sign): this

  /**
   * Attach object state to object
   */
  attachState(state: ST): this

  /**
   * Set image to render in areas that are clipping out of bounds
   */
  setClipOverflowElement(el: HTMLImageElement): this

  /**
   * Generated fabric object for the object's object border
   */
  objectBorderForm(): DrawableMixin | null
}

interface MaybeSvg extends fabric.Object {
  toSVG(receiver?: (svg: string) => string): string
  _createBaseSVGMarkup(objectMarkup: string[], options: any): string
}

export default function ObjectMixin<T extends Constructor<MaybeSvg>, ST extends SignImageState | SignTextState>(
  Base: T,
  _StateType: ST
) {
  return class extends Base implements ObjectMixin<ST> {
    state: ST
    private sign: Sign
    private classList: ObjectClasses[] = []
    private clipOverflowElement: HTMLImageElement
    placeholder: string
    isPlaceholder: boolean

    get classes() {
      return this.classList
    }

    get attachedSign() {
      return this.sign
    }

    get hasObjectBorder() {
      return !!this.state.border && !!this.state.border.shape
    }

    private get objectBorderParams() {
      const { color, borderColor } = this.state.border || {}

      const params = {
        fill: color,
        strokeWidth: this.objectBorderStrokeWidth(),
        stroke: borderColor,
        evented: false,
        hasControls: false,
        hasBorders: false,
        originX: this.originX,
        originY: this.originY,
        left: this.left,
        top: this.top,
        angle: this.angle,
        opacity: 1,
        name: "objectborder ignore",
      }

      return params
    }

    render(ctx: CanvasRenderingContext2D) {
      timingMarkStart("ObjectMixin:render")

      const border = this.objectBorderForm()

      if (border) {
        border.canvas = this.canvas
        border.opacity = this.opacity
        border.render(ctx)
      }

      super.render(ctx)

      timingMarkEnd("ObjectMixin:render")
    }

    toSVG(receiver?: (svg: string) => string) {
      if (super.toSVG !== undefined) {
        const finalSVG = this.processSVG(super.toSVG())

        if (receiver) {
          return receiver(finalSVG)
        } else {
          return finalSVG
        }
      }

      return ""
    }

    addClass(...classNames: ObjectClasses[]) {
      this.classList.push(...classNames)
      return this
    }

    attachSign(sign: Sign) {
      this.sign = sign
      return this
    }

    attachState(state: ST) {
      this.state = state
      return this
    }

    addPlaceholder(placeholder: string = "", isPlaceholder: boolean = false) {
      this.placeholder = placeholder
      this.isPlaceholder = isPlaceholder
      return this
    }

    getClipOverflowElement() {
      return this.clipOverflowElement
    }

    setClipOverflowElement(el: HTMLImageElement) {
      this.clipOverflowElement = el
      return this
    }

    objectBorderForm() {
      if (!this.state.border || !this.state.border.shape) {
        return null
      }

      const { shape, size } = this.state.border || {}
      const { triangleSize, objWidth, objHeight, strokeExtra, maxDimension } = this.objectBorderFormParams()
      let form: DrawableMixin | null = null

      if (shape === "ellipse") {
        const radius = (maxDimension / 2) * size
        form = new CircleObject({ ...this.objectBorderParams, radius })
      } else if (["rect", "round"].includes(shape)) {
        const width = (objWidth + strokeExtra) * size
        const height = (objHeight + strokeExtra) * size
        const x = -width / 2
        const y = height / 2
        const points = [
          { x, y },
          { x: x + width, y },
          { x: x + width, y: y - height },
          { x, y: y - height },
          { x, y },
        ]

        if (shape === "rect") {
          form = new PolygonObject(points, this.objectBorderParams)
        } else {
          const radius = Math.min(points[1].x, points[1].y) / 5
          const pathString = [
            "M",
            points[0].x,
            points[0].y,
            "L",
            points[1].x,
            points[1].y,
            "L",
            points[2].x,
            points[2].y,
            "L",
            points[3].x,
            points[3].y,
            "Z",
          ].join(" ")
          const path = pathRounding(pathString, radius).join("")
          form = new PathObject(path, this.objectBorderParams)
        }
      } else if (shape === "triangle") {
        const x = -triangleSize / 2
        const y = triangleSize / 2
        const points = [
          { x, y },
          { x: x + triangleSize, y },
          { x: x + triangleSize / 2, y: y - triangleSize },
          { x, y },
        ]

        const top = this.triangleTop()
        form = new PolygonObject(points, { ...this.objectBorderParams, top })
      }

      if (form) {
        form.clipPath = this.clipPath
        form.data = {}
      }

      return form
    }

    // https://github.com/fabricjs/fabric.js/blob/v460/src/mixins/object.svg_export.js#L197
    _createBaseSVGMarkup(objectMarkup: string[], options: any) {
      if (this.classList.length > 0) {
        const index = objectMarkup.indexOf("COMMON_PARTS")

        if (index >= 0) {
          const attrs = [`class="${arrayUniq(this.classList).join(" ")}"`]

          if (typeof this.fill === "string" && isSpotColor(this.fill)) {
            attrs.push(`spotfill="${spotColorName(this.fill)}"`)
          }

          if (typeof this.stroke === "string" && isSpotColor(this.stroke)) {
            attrs.push(`spotstroke="${spotColorName(this.stroke)}"`)
          }

          objectMarkup.splice(index, 0, `${attrs.join(" ")} `)
        }
      }

      return super._createBaseSVGMarkup(objectMarkup, options)
    }

    private processSVG(baseSvg: string) {
      if (!this.sign) {
        return baseSvg
      }

      let objectBorder = ""
      const border = this.objectBorderForm()

      if (border) {
        const type = (this.type || "") as DrawableClasses
        const shape = (this.state.border?.shape || "") as DrawableClasses
        border.addClass("objectborder", type, shape)
        objectBorder = border.toSVG()
      }

      return `<g>${objectBorder}${baseSvg}</g>`
    }

    private objectBorderFormParams() {
      const objWidth = this.getScaledWidth()
      const objHeight = this.getScaledHeight()
      const size = this.state.border?.size || 0
      const strokeExtra = this.objectBorderStrokeWidth() * 2.5
      const maxDimension = Math.max(objWidth, objHeight) + strokeExtra
      const extra = this.type === "i-text" ? 1.2 : 1.6
      const triangleSize = maxDimension * extra * size

      return {
        objWidth,
        objHeight,
        strokeExtra,
        maxDimension,
        triangleSize,
      }
    }

    private triangleTop() {
      const { triangleSize, objHeight } = this.objectBorderFormParams()

      return this.type === "i-text"
        ? (this.top || 0) - (triangleSize / 2) * 1 + this.getScaledHeight() / 2 + this.objectBorderStrokeWidth() / 2
        : (this.top || 0) - objHeight / 4 - this.objectBorderStrokeWidth()
    }

    private objectBorderStrokeWidth() {
      const base = this.getSignBorderThickness()

      if (base) {
        return base * (this.state.border?.thickness || 0)
      }

      return 0
    }

    private getSignBorderThickness() {
      if (this.sign) {
        return Math.min(this.sign.state.widthPx, this.sign.state.heightPx) * 0.042
      }

      return null
    }
  }
}
